import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Shield, Lock, Award, BookOpen, Building, FileText, CheckCircle, ArrowLeft } from 'lucide-react';

const AboutSafety = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#0a1628] text-white">
      <nav className="bg-[#0d1d35] border-b border-[#1a2d4a]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <h1 className="text-2xl font-bold">About Bethereum</h1>
          <Button variant="ghost" onClick={() => navigate('/')} className="text-gray-400 hover:text-white">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Home
          </Button>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="text-center mb-12">
          <Shield className="h-16 w-16 text-[#b8860b] mx-auto mb-4" />
          <h2 className="text-4xl font-bold mb-4">Private Credit Infrastructure</h2>
          <p className="text-xl text-gray-400">
            Institutional-Grade Securities Finance Platform
          </p>
        </div>

        <div className="space-y-6">
          <Card className="bg-[#0d1d35] border-[#1a2d4a]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-white">
                <Building className="h-6 w-6 text-[#b8860b]" />
                What is Bethereum?
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-gray-300">
              <p>
                Bethereum is a <strong className="text-white">private credit infrastructure platform</strong> designed for 
                accredited investors and qualified institutional participants to conduct securities finance operations.
              </p>
              <p>
                We operate on <strong className="text-white">Ethereum Mainnet</strong> with deployed smart contracts, enabling:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong className="text-white">Securities Finance</strong> - Lending and borrowing with BETH collateral</li>
                <li><strong className="text-white">Repo Transactions</strong> - Repurchase agreements for short-term financing</li>
                <li><strong className="text-white">Swaps</strong> - Interest rate and credit derivative contracts</li>
                <li><strong className="text-white">Structured Credit</strong> - Prime brokerage and margin lending facilities</li>
                <li><strong className="text-white">Collateral Management</strong> - BETH-backed credit enhancement</li>
              </ul>
            </CardContent>
          </Card>

          <Card className="bg-[#0d1d35] border-[#1a2d4a]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-white">
                <Lock className="h-6 w-6 text-[#b8860b]" />
                Platform Security
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-gray-300">
              <p><strong className="text-white">Built for institutional standards:</strong></p>
              <div className="space-y-3">
                <div className="p-4 bg-[#b8860b]/10 border border-[#b8860b]/30 rounded-lg">
                  <h4 className="font-semibold text-[#b8860b] mb-2 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" /> Non-Custodial Architecture
                  </h4>
                  <p className="text-sm">
                    You maintain full control of your assets at all times. Smart contracts execute 
                    transactions only with your explicit approval via wallet signature.
                  </p>
                </div>
                <div className="p-4 bg-[#b8860b]/10 border border-[#b8860b]/30 rounded-lg">
                  <h4 className="font-semibold text-[#b8860b] mb-2 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" /> Ethereum Mainnet
                  </h4>
                  <p className="text-sm">
                    All smart contracts are deployed on Ethereum mainnet with real blockchain 
                    security. Fully transparent and verifiable on Etherscan.
                  </p>
                </div>
                <div className="p-4 bg-[#b8860b]/10 border border-[#b8860b]/30 rounded-lg">
                  <h4 className="font-semibold text-[#b8860b] mb-2 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" /> Accredited Investors Only
                  </h4>
                  <p className="text-sm">
                    Platform access requires SEC-compliant accreditation verification. 
                    We verify investor status through income, net worth, or professional credentials.
                  </p>
                </div>
                <div className="p-4 bg-[#b8860b]/10 border border-[#b8860b]/30 rounded-lg">
                  <h4 className="font-semibold text-[#b8860b] mb-2 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" /> Compliance First
                  </h4>
                  <p className="text-sm">
                    BETH is a utility token, NOT a security. The platform follows SEC Rule 501 
                    and maintains full regulatory compliance for private credit operations.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#0d1d35] border-[#1a2d4a]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-white">
                <Award className="h-6 w-6 text-[#b8860b]" />
                Business Model
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-gray-300">
              <p>Revenue is generated through institutional service fees:</p>
              <ul className="space-y-2">
                <li className="flex items-start gap-2">
                  <span className="text-[#b8860b]">•</span>
                  <span><strong className="text-white">Credit Enhancement Fees:</strong> Fees for using BETH as conditional collateral</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#b8860b]">•</span>
                  <span><strong className="text-white">Management Fees:</strong> Standard fund management fees (1-2% annually)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#b8860b]">•</span>
                  <span><strong className="text-white">Performance Fees:</strong> Percentage of profits (20% standard)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#b8860b]">•</span>
                  <span><strong className="text-white">Origination Fees:</strong> Fees for setting up credit facilities</span>
                </li>
              </ul>
              <p className="text-sm italic bg-[#1a2d4a]/50 p-3 rounded">
                BETH tokens are utility tokens with no investment value outside their collateral function. 
                This is private credit infrastructure, not a retail investment platform.
              </p>
            </CardContent>
          </Card>

          <Card className="bg-[#0d1d35] border-[#1a2d4a]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-white">
                <FileText className="h-6 w-6 text-[#b8860b]" />
                Smart Contract Verification
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-gray-300">
              <div className="space-y-3">
                <div>
                  <h4 className="font-semibold mb-2 text-white">Deployed Contracts (Ethereum Mainnet):</h4>
                  <div className="space-y-2 text-xs font-mono bg-[#1a2d4a]/50 p-3 rounded">
                    <p className="flex justify-between">
                      <span className="text-gray-400">BethUtilityToken:</span>
                      <a href="https://etherscan.io/address/0x6822824c5349bf005e353ec5dda3fa34595ea811" target="_blank" rel="noopener noreferrer" className="text-[#b8860b] hover:underline">0x6822824c5349bf005e353ec5dda3fa34595ea811</a>
                    </p>
                    <p className="flex justify-between">
                      <span className="text-gray-400">CollateralVault:</span>
                      <a href="https://etherscan.io/address/0xe86c1f0fd87165cbde68714e6f4cb152e7e5707d" target="_blank" rel="noopener noreferrer" className="text-[#b8860b] hover:underline">0xe86c1f0fd87165cbde68714e6f4cb152e7e5707d</a>
                    </p>
                    <p className="flex justify-between">
                      <span className="text-gray-400">CreditLineContract:</span>
                      <a href="https://etherscan.io/address/0x8abfe72db538c6ff99452a910b9bc7a980ef0fdd" target="_blank" rel="noopener noreferrer" className="text-[#b8860b] hover:underline">0x8abfe72db538c6ff99452a910b9bc7a980ef0fdd</a>
                    </p>
                    <p className="flex justify-between">
                      <span className="text-gray-400">LiquidationEngine:</span>
                      <a href="https://etherscan.io/address/0x65e67b63513de0b2bf463c47a2a284254e23fcda" target="_blank" rel="noopener noreferrer" className="text-[#b8860b] hover:underline">0x65e67b63513de0b2bf463c47a2a284254e23fcda</a>
                    </p>
                    <p className="flex justify-between">
                      <span className="text-gray-400">TreasuryManager:</span>
                      <a href="https://etherscan.io/address/0xc21cd1629a88fe99dbf4cf5d202c04bd493cbf2c" target="_blank" rel="noopener noreferrer" className="text-[#b8860b] hover:underline">0xc21cd1629a88fe99dbf4cf5d202c04bd493cbf2c</a>
                    </p>
                  </div>
                  <p className="text-sm mt-2">
                    Verify all contracts on <a href="https://etherscan.io" target="_blank" rel="noopener noreferrer" className="text-[#b8860b] hover:underline">Etherscan</a>
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-r from-[#b8860b]/10 to-[#b8860b]/5 border-[#b8860b]/30">
            <CardContent className="py-8 text-center">
              <h3 className="text-2xl font-bold mb-4 text-white">Ready to Get Started?</h3>
              <p className="text-gray-300 mb-6">
                Apply for platform access through our accreditation verification process.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button className="bg-[#b8860b] hover:bg-[#9a7209]" onClick={() => navigate('/onboarding')}>
                  Apply for Access
                </Button>
                <Button variant="outline" className="border-[#b8860b] text-[#b8860b] hover:bg-[#b8860b]/10" onClick={() => navigate('/investor-guide')}>
                  View Investor Guide
                </Button>
              </div>
              <p className="text-gray-500 text-sm mt-4">
                For accredited investors and qualified institutional participants only
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AboutSafety;
